import { Button, ButtonTypes, Loading, TextBox } from "@repo/ui";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTrainingApi, useTrainingQueries } from "../../../api/trainingApi";
import { useRepContext } from "../../../contexts/editableItemContext";
import { buildSaveChain } from "../../../helpers/saveChain";
import MindflashCustomField from "../../../models/mindflashCustomField";
import { Props } from "../types";

export function MindFlashCustomFields(props: Props) {
	const { repId } = useRepContext();
	const { saveMindflashCustomFields } = useTrainingApi();
	const { useMindflashCustomFieldsQuery } = useTrainingQueries();
	const [state, setState] = useState<Map<number, string>>(new Map<number, string>());
	const { data, loaded } = useMindflashCustomFieldsQuery();

	useEffect(() => {
		if (!state.size && data?.length) {
			const map = new Map();
			for (const field of data) map.set(field.fieldIndex, field.value);
			setState(map);
		}
	}, [data]);

	const saveFn = useCallback((fields: MindflashCustomField[]) => saveMindflashCustomFields(repId, fields), [repId]);
	const saveChain = useMemo(() => buildSaveChain(saveFn, "Saving Custom Fields").execute, [saveFn]);
	const customFieldsMutation = useMutation({ mutationFn: (fields: MindflashCustomField[]) => saveChain(fields) });

	const changeHandler = useCallback(
		(value: string, id: string) => {
			state.set(parseInt(id), value);
			setState(state);
		},
		[state],
	);

	const handleSave = useCallback(() => {
		const fields = Array.from(state, ([id, value]) => ({ fieldIndex: id, value }) as MindflashCustomField);

		customFieldsMutation.mutate(fields);
	}, [state]);

	function renderSaveButton() {
		if (props.readOnly) return null;
		return (
			<Loading loading={customFieldsMutation.isPending} message="Saving...">
				<Button types={[ButtonTypes.Primary]} onClick={handleSave}>
					Save
				</Button>
			</Loading>
		);
	}

	return (
		<>
			<h3>TrakStar Custom Fields</h3>
			<Loading loading={!loaded}>
				{data?.map(renderField)}
				{renderSaveButton()}
				{customFieldsMutation.isError && <div>Failed to save</div>}
			</Loading>
		</>
	);

	function renderField(field: MindflashCustomField) {
		const segmentFields = ["Brand", "Channel", "Vendor", "Marketer", "State", "Branch", "Office"];
		const disabled = segmentFields.includes(field.name);
		return (
			<div key={field.fieldIndex}>
				<TextBox
					id={field.fieldIndex.toString()}
					label={field.name}
					value={state.get(field.fieldIndex)}
					changeHandler={changeHandler}
					disabled={props.readOnly || disabled}
				/>
			</div>
		);
	}
}
